import type {
  AvailableStart,
  BookingKind,
  Locale,
  RoomKey,
  RoomBookingAvailability,
  RoomState,
  ScheduleDay,
  ScheduleResponse,
  ScheduleSlot,
  TreatmentDuration,
} from "../types";

const API_URL = import.meta.env.VITE_SCHEDULE_API_URL?.trim();
const PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY?.trim();
const CACHE_PREFIX = "restore-booking-schedule-v2:";
const CACHE_MAX_AGE_MS = 5 * 60_000;
const pendingRequests = new Map<string, Promise<ScheduleResponse>>();

function isRoomState(value: unknown): value is RoomState {
  if (typeof value !== "object" || value === null) return false;
  const status = (value as RoomState).status;
  return status === "free" || status === "busy" || status === "closed";
}

function isSlot(value: unknown): value is ScheduleSlot {
  if (typeof value !== "object" || value === null) return false;
  const slot = value as ScheduleSlot;
  return (
    /^([01]\d|2[0-3]):[0-5]\d$/.test(slot.time) &&
    isRoomState(slot.room1) &&
    isRoomState(slot.room2)
  );
}

function isDay(value: unknown): value is ScheduleDay {
  if (typeof value !== "object" || value === null) return false;
  const day = value as ScheduleDay;
  return (
    typeof day.label === "string" &&
    Array.isArray(day.slots) &&
    day.slots.every(isSlot)
  );
}

function isScheduleResponse(value: unknown): value is ScheduleResponse {
  if (typeof value !== "object" || value === null) return false;
  const schedule = value as ScheduleResponse;
  return (
    typeof schedule.generatedAt === "string" &&
    schedule.timezone === "Asia/Jerusalem" &&
    schedule.source === "restore_calendar" &&
    Array.isArray(schedule.days) &&
    schedule.days.every(isDay)
  );
}

type CachedSchedule = {
  cachedAt: number;
  value: ScheduleResponse;
};

function cacheKey(startDate?: string): string {
  return `${CACHE_PREFIX}${startDate || "default"}`;
}

export function readScheduleCache(startDate?: string): ScheduleResponse | null {
  if (typeof sessionStorage === "undefined") return null;

  try {
    const raw = sessionStorage.getItem(cacheKey(startDate));
    if (!raw) return null;
    const cached = JSON.parse(raw) as CachedSchedule;
    if (
      typeof cached.cachedAt !== "number" ||
      Date.now() - cached.cachedAt > CACHE_MAX_AGE_MS ||
      !isScheduleResponse(cached.value)
    ) {
      sessionStorage.removeItem(cacheKey(startDate));
      return null;
    }
    return cached.value;
  } catch {
    return null;
  }
}

function writeScheduleCache(startDate: string | undefined, value: ScheduleResponse) {
  if (typeof sessionStorage === "undefined") return;

  try {
    const cached: CachedSchedule = { cachedAt: Date.now(), value };
    const serialized = JSON.stringify(cached);
    sessionStorage.setItem(cacheKey(startDate), serialized);
    for (const day of value.days) {
      const iso = day.label.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
      if (iso) sessionStorage.setItem(cacheKey(`${iso[3]}-${iso[2]}-${iso[1]}`), serialized);
    }
  } catch {
    // Storage can be unavailable in private browsing. The live request still works.
  }
}

async function requestSchedule(
  requestUrl: URL,
  signal: AbortSignal | undefined,
  startDate: string | undefined,
): Promise<ScheduleResponse> {
  const response = await fetch(requestUrl, {
    method: "GET",
    headers: {
      apikey: PUBLISHABLE_KEY!,
      Accept: "application/json",
    },
    signal,
  });

  if (!response.ok) {
    throw new Error(`schedule_request_failed_${response.status}`);
  }

  const payload: unknown = await response.json();
  if (!isScheduleResponse(payload)) {
    throw new Error("schedule_response_invalid");
  }

  writeScheduleCache(startDate, payload);
  return payload;
}

export async function fetchSchedule(
  signal?: AbortSignal,
  forceFresh = false,
  startDate?: string,
): Promise<ScheduleResponse> {
  if (!API_URL || !PUBLISHABLE_KEY) {
    throw new Error("schedule_configuration_missing");
  }

  const requestUrl = new URL(API_URL);
  if (forceFresh) requestUrl.searchParams.set("fresh", "1");
  if (startDate) requestUrl.searchParams.set("start", startDate);

  if (forceFresh || signal) {
    return requestSchedule(requestUrl, signal, startDate);
  }

  const key = requestUrl.toString();
  const pending = pendingRequests.get(key);
  if (pending) return pending;

  const request = requestSchedule(requestUrl, undefined, startDate).finally(() => {
    pendingRequests.delete(key);
  });
  pendingRequests.set(key, request);
  return request;
}

export function parseCalendarLabel(label: string): Date | null {
  const match = label.trim().match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (!match) return null;

  const [, day, month, year] = match;
  const parsed = new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    12,
    0,
    0,
  );

  if (
    parsed.getFullYear() !== Number(year) ||
    parsed.getMonth() !== Number(month) - 1 ||
    parsed.getDate() !== Number(day)
  ) {
    return null;
  }

  return parsed;
}

export function formatDay(label: string, locale: Locale) {
  const parsed = parseCalendarLabel(label);
  if (!parsed) return { weekday: label, date: "" };

  const localeTag = locale === "he" ? "he-IL" : "en-GB";
  return {
    weekday: new Intl.DateTimeFormat(localeTag, { weekday: "short" }).format(
      parsed,
    ),
    date: new Intl.DateTimeFormat(localeTag, {
      day: "numeric",
      month: "short",
    }).format(parsed),
  };
}

export function countFreeRoomWindows(day?: ScheduleDay): number {
  if (!day) return 0;
  return day.slots.reduce((total, slot) => {
    return (
      total +
      Number(slot.room1.status === "free") +
      Number(slot.room2.status === "free")
    );
  }, 0);
}

function timeToMinutes(time: string): number {
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 60 + minutes;
}

export type AvailabilityOptions = {
  bookingKind?: BookingKind;
  minimumLeadMinutes?: number;
  now?: Date;
  timeZone?: string;
};

type DateTimeParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
};

function parseCalendarLabelParts(
  label: string,
): Omit<DateTimeParts, "hour" | "minute"> | null {
  const match = label.trim().match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (!match) return null;

  const [, day, month, year] = match;
  return {
    year: Number(year),
    month: Number(month),
    day: Number(day),
  };
}

function getPartsInTimeZone(date: Date, timeZone: string): DateTimeParts {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);

  const values = Object.fromEntries(
    parts
      .filter((part) => part.type !== "literal")
      .map((part) => [part.type, Number(part.value)]),
  );

  return {
    year: values.year,
    month: values.month,
    day: values.day,
    hour: values.hour,
    minute: values.minute,
  };
}

function wallClockToEpochMs(parts: DateTimeParts, timeZone: string): number {
  const requestedWallClock = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
  );
  let epoch = requestedWallClock;

  // Two passes resolve the local wall clock to the correct UTC instant,
  // including the current Jerusalem daylight-saving offset.
  for (let pass = 0; pass < 2; pass += 1) {
    const observed = getPartsInTimeZone(new Date(epoch), timeZone);
    const observedWallClock = Date.UTC(
      observed.year,
      observed.month - 1,
      observed.day,
      observed.hour,
      observed.minute,
    );
    epoch += requestedWallClock - observedWallClock;
  }

  return epoch;
}

function meetsMinimumLeadTime(
  dayLabel: string,
  time: string,
  {
    minimumLeadMinutes = 0,
    now = new Date(),
    timeZone = "Asia/Jerusalem",
  }: AvailabilityOptions,
): boolean {
  if (minimumLeadMinutes <= 0) return true;

  const dateParts = parseCalendarLabelParts(dayLabel);
  if (!dateParts) return false;

  const [hour, minute] = time.split(":").map(Number);
  const startEpoch = wallClockToEpochMs(
    { ...dateParts, hour, minute },
    timeZone,
  );
  return startEpoch - now.getTime() >= minimumLeadMinutes * 60_000;
}

function getContinuousFreeMinutes(
  day: ScheduleDay,
  startIndex: number,
  room: RoomKey,
): number {
  const startMinutes = timeToMinutes(day.slots[startIndex].time);
  let windows = 0;

  for (let offset = 0; startIndex + offset < day.slots.length; offset += 1) {
    const slot = day.slots[startIndex + offset];
    if (!slot) break;
    if (timeToMinutes(slot.time) !== startMinutes + offset * 60) break;
    if (slot[room].status !== "free") break;
    windows += 1;
  }

  return windows * 60;
}

/**
 * Explains why one room can or cannot be selected at a particular start time.
 * The UI and the final availability check both use this function, preventing a
 * room from being labelled "available" while its button is disabled.
 */
export function getRoomBookingAvailability(
  day: ScheduleDay | undefined,
  time: string,
  room: RoomKey,
  duration: TreatmentDuration | null,
  options: AvailabilityOptions = {},
): RoomBookingAvailability {
  if (!day || !duration) {
    return {
      available: false,
      reason: "insufficient_duration",
      maxContinuousMinutes: 0,
    };
  }

  const startIndex = day.slots.findIndex((slot) => slot.time === time);
  if (startIndex < 0) {
    return {
      available: false,
      reason: "insufficient_duration",
      maxContinuousMinutes: 0,
    };
  }

  const roomState = day.slots[startIndex][room];
  if (roomState.status === "busy") {
    return { available: false, reason: "busy", maxContinuousMinutes: 0 };
  }
  if (roomState.status === "closed") {
    return { available: false, reason: "closed", maxContinuousMinutes: 0 };
  }

  const maxContinuousMinutes = getContinuousFreeMinutes(day, startIndex, room);

  if (!meetsMinimumLeadTime(day.label, time, options)) {
    return { available: false, reason: "minimum_lead", maxContinuousMinutes };
  }

  if (maxContinuousMinutes < duration) {
    return {
      available: false,
      reason: "insufficient_duration",
      maxContinuousMinutes,
    };
  }

  return { available: true, reason: "available", maxContinuousMinutes };
}

/**
 * Uses the hourly room feed conservatively: longer treatments are offered only
 * when the same room stays free in every hourly window they touch.
 */
export function getAvailableStartTimes(
  day: ScheduleDay | undefined,
  duration: TreatmentDuration | null,
  options: AvailabilityOptions = {},
): AvailableStart[] {
  if (!day || !duration) return [];

  return day.slots.flatMap((slot) => {
    const rooms = (["room1", "room2"] as const).filter(
      (room) =>
        getRoomBookingAvailability(day, slot.time, room, duration, options)
          .available,
    );

    if (options.bookingKind === "couple") {
      return rooms.length === 2 ? [{ time: slot.time, rooms: [...rooms] }] : [];
    }

    return rooms.length > 0 ? [{ time: slot.time, rooms: [...rooms] }] : [];
  });
}
